import EventEmitter from "@/lib/EventEmitter";
import { getStreamConstraints } from "@/modules/stream/constraints";
import DbController from "./modules/DbController";
import TimeRanges from "./modules/TimeRanges";
import Playlist from "./modules/Playlist";
import Transcoder from "./modules/Transcoder";
import { APPEND_THRESHOLD_MS, DB_TIMEOUT, SEGMENT_LENGTH } from "./const";
import { getSegmentEndTime, initHasVideoTrack, mimeType } from "./extensions";
import DB from "./DB";
import { useStation } from "camera/store/station";
import { cameraStore } from "camera/store/camera";
import { getReplayDuration } from "@/modules/replay/constraints";
import { addTimeout } from "@/utils";
import CrashReporter from "@/modules/app/CrashReporter";
import { useApp } from "@/store/app";
import { pendingEventsStore } from "camera/store/pendingEvents";

const setStatus = useApp.getState().setStatus;

export default class Recorder {
  private emitter = new EventEmitter();
  on = this.emitter.on;
  off = this.emitter.off;

  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;

  private timeout: NodeJS.Timeout | null = null;
  private hasCreatedInitFile = false;

  private currentInitId: number | null = null;
  private currentInitFilename: string | null = null;
  private currentInit: HlsDataItem | null = null;

  private canStore = true;
  private shouldPrepareStoring = false;
  private isCheckingDb = false;

  private isTranscoding = false;
  private session = 0;

  private uploadDateRange: { start: number | null; end: number | null } = {
    start: null,
    end: null
  };

  private sourceDateMs: number = Date.now();
  private trancoderQueue: { data: Blob; trueTime: number; session: number }[] = [];
  private videoAppendInMs = 0;
  private trueSegmentEndTime = 0;

  private get sourceDateIso() {
    return new Date(Math.round(this.sourceDateMs)).toISOString();
  }

  db = new DB();
  private dbController = new DbController(this.db);
  playlist = new Playlist(this.dbController);
  private transcoder = new Transcoder(this.playlist);

  status: "idle" | "recording" = "idle";

  private getStreamConstraints = () => useStation.getState().settings.videoQuality;
  private getReplayDuration = () => useStation.getState().settings.replayDuration;

  createNewMediaRecorder = () => {
    if (!this.stream) {
      log.err("Couldn't create new mediaRecorder, 'this.stream' is 'null' or 'undefined'");
      return;
    }
    if (this.status === "recording") {
      log.warn("createNewMediaRecorder skipped: recorder already recording");
      return;
    }
    this.mediaRecorder?.removeEventListener("dataavailable", this.onDataAvailable);
    const { bitrate, audioBitrate } = getStreamConstraints(this.getStreamConstraints());
    log.recorder("Creating new mediaRecorder with params:", this.stream.getVideoTracks()[0].getConstraints(), bitrate);
    this.mediaRecorder = new MediaRecorder(this.stream, {
      videoBitsPerSecond: bitrate,
      audioBitsPerSecond: audioBitrate,
      mimeType
    });
    this.mediaRecorder.addEventListener("dataavailable", this.onDataAvailable);
  };

  init = async (stream: MediaStream) => {
    this.stream = stream;
    try {
      const { bitrate, audioBitrate } = getStreamConstraints(this.getStreamConstraints());
      this.mediaRecorder = new MediaRecorder(this.stream, {
        videoBitsPerSecond: bitrate,
        audioBitsPerSecond: audioBitrate,
        mimeType
      });
      this.mediaRecorder.addEventListener("dataavailable", this.onDataAvailable);
      log.recorder("Created mediaRecorder, continuing");
      await addTimeout(this.db.init, DB_TIMEOUT)
        .then(() => log.recorder("db.init finished"))
        .catch((err) => log.err("db.init didn't finish, recording without the DB", err));
      await this.transcoder.init();
      log.recorder("transcoder.init finished");
      await addTimeout(() => this.dbController.deleteOlderThan(getReplayDuration(this.getReplayDuration())), DB_TIMEOUT)
        .then(() => log.recorder("dbController.deleteOlderThan finished"))
        .catch((err) => log.err("dbController.deleteOlderThan didn't finish in time, continuing", err));
      await this.playlist.loadGapFiles();
      log.recorder("playlist.loadGapFiles finished");

      TimeRanges.init(this.dbController);
    } catch (err) {
      log.err("Recorder failed to initialize");
      log.err(err);
      if (!isDev) setStatus("FAILURE");
    }
  };

  start = async () => {
    await this.startRecordingSourceVideo();
  };

  stop = () => {
    log.recorder("Stop called");
    if (this.timeout) clearTimeout(this.timeout);
    this.status = "idle";
    cameraStore().setIsRecording(false);
    this.session++;
    this.trancoderQueue = [];
    this.videoAppendInMs = 0;
    this.mediaRecorder?.stop();
  };

  startMarkingDataForUpload = () => {
    if (this.uploadDateRange.start && this.uploadDateRange.end) {
      this.uploadDateRange.end = null;
    } else {
      this.uploadDateRange.start = new Date().getTime() - 2500;
      this.uploadDateRange.end = null;
    }
  };
  stopMarkingDataForUpload = () => (this.uploadDateRange.end = new Date().getTime() + 2500);
  isMarkingDataForUpload = () => this.status === "recording" && Boolean(this.uploadDateRange.start);

  private getShouldBeUploaded = (item: HlsMetaItem | HlsDataItem) => {
    const startTime = new Date(item.createdAt).getTime();
    const endTime = getSegmentEndTime(item);

    const uploadFrom = this.uploadDateRange.start;
    const uploadUntil = this.uploadDateRange.end;

    if (!uploadFrom && !uploadUntil) return false;
    if (uploadFrom && !uploadUntil) return endTime >= uploadFrom;
    if (uploadFrom && uploadUntil) return endTime >= uploadFrom && startTime <= uploadUntil;
    return false;
  };

  private createSourceVideo = () => {
    if (this.status === "idle") {
      log.warn("Recorder is in idle state, but createSourceVideo fired");
      return;
    }

    try {
      this.mediaRecorder?.start();
    } catch (err) {
      log.err("Failed to start MediaRecorder", err);
      return;
    }

    this.timeout = setTimeout(() => {
      this.trueSegmentEndTime = new Date().getTime();
      this.mediaRecorder?.stop();
      this.createSourceVideo();
    }, SEGMENT_LENGTH);
  };

  private startRecordingSourceVideo = async () => {
    if (this.status === "recording") return;
    log.recorder("Starting creating segments");
    this.hasCreatedInitFile = false;
    this.status = "recording";
    this.sourceDateMs = Date.now();
    const session = this.session;
    await this.prepareStoring();
    if (session !== this.session) {
      log.recorder("Recording session changed during start, aborting");
      return;
    }
    this.createSourceVideo();
    cameraStore().setIsRecording(true);
  };

  private onDataAvailable = async (event: BlobEvent) => {
    log.recorder("OnDataAvailable fired with data", event.data);
    if (this.status === "idle") {
      log.warn("OnDataAvailable fired in 'idle' status, ignoring");
      return;
    }

    this.dbController.deleteOlderThan(getReplayDuration(this.getReplayDuration()), () =>
      this.playlist.addToDiscontinuitySequences(1)
    );

    const trueSegmentEndTime = this.trueSegmentEndTime;
    const session = this.session;
    if (this.isTranscoding) {
      log.recorder("Transcoding in progress, adding to queue...", this.trancoderQueue.length);
      this.trancoderQueue.push({ data: event.data, trueTime: trueSegmentEndTime, session });
      return;
    }

    try {
      this.isTranscoding = true;
      await this.transcode(event.data, trueSegmentEndTime, session);

      while (this.trancoderQueue.length > 0) {
        log.recorder("Found items in queue: ", this.trancoderQueue);
        const { data, trueTime, session } = this.trancoderQueue.shift()!;
        await this.transcode(data, trueTime, session);
      }
      this.isTranscoding = false;
    } catch (err) {
      log.err(err);
      this.isTranscoding = false;
      if (!isDev) restartApp({ useLimiter: true, cause: "recorder-transcode-failed" });
    }
  };

  private transcode = async (blob: Blob, trueSegmentEndTime: number, session: number) => {
    if (session !== this.session) {
      log.recorder("Discarding chunk from a previous recording session");
      return;
    }
    if (this.shouldPrepareStoring) await this.prepareStoring();
    const { initData, segmentsData } = await this.transcoder.transcode({
      blob,
      includeInitData: !this.hasCreatedInitFile,
      appendInMs: this.videoAppendInMs || null
    });
    if (session !== this.session) {
      log.recorder("Recording session changed during transcoding, discarding result");
      return;
    }

    if (initData && !initHasVideoTrack(initData)) {
      log.err("Transcoded init has no video track, discarding chunk");
      CrashReporter.sendMessage("Transcoded init has no video track, discarding chunk");
      this.sourceDateMs = trueSegmentEndTime;
      return;
    }

    let initFilename: string | undefined;

    if (initData) {
      log.recorder("Got new INIT data");
      const index = this.playlist.getLatestInitIndex();
      initFilename = `i${index}.mp4`;

      this.currentInitFilename = initFilename;
      this.currentInitId = Math.round(this.sourceDateMs);

      const initMetaPayload: HlsMetaItem = {
        filename: initFilename,
        createdAt: this.sourceDateIso,
        duration: null,
        discontinuity: false,
        index
      };
      const initDataPayload: HlsDataItem = {
        filename: initFilename,
        data: initData,
        createdAt: this.sourceDateIso,
        shouldUpload: 0,
        uploaded: 0,
        selectForUpload: 0,
        initFilename: null,
        initId: this.currentInitId,
        duration: null,
        discontinuity: false
      };
      await this.storeToDb(initMetaPayload, initDataPayload);
      this.currentInit = initDataPayload;
      this.playlist.addToNextInitIndex(1);
      this.hasCreatedInitFile = true;
    }

    const segmentStartIndex = this.playlist.getLatestSegmentIndex();

    let i = 0;
    for (const segment of segmentsData) {
      const { data, duration } = segment;
      if (!duration || !data) {
        log.err("No duration or data:", duration, data);
        CrashReporter.sendException("No duration or data", { key: "err", extra: { duration, dataSize: data?.length } });
        if (!isDev) restartApp({ cause: "recorder-empty-segment" });
        continue;
      }
      if (Number(duration) < 1) log.warn("Segment duration is", duration);
      const index = segmentStartIndex + i;
      const segmentMetaPayload: HlsMetaItem = {
        filename: `s${index}.m4s`,
        duration,
        createdAt: this.sourceDateIso,
        discontinuity: i === 0,
        index
      };

      const shouldMarkForUpload = this.getShouldBeUploaded(segmentMetaPayload);
      log.recorder(`Should upload ${segmentMetaPayload.filename}:`, shouldMarkForUpload);
      if (shouldMarkForUpload) {
        const durationMs = Math.round(parseFloat(duration!) * 1000);
        pendingEventsStore().setLastUploadedSegmentEndTime(
          new Date(segmentMetaPayload.createdAt).getTime() + durationMs
        );
      }

      const segmentDataPayload: HlsDataItem = {
        filename: `s${index}.m4s`,
        data,
        createdAt: segmentMetaPayload.createdAt,
        shouldUpload: shouldMarkForUpload ? 1 : 0,
        selectForUpload: shouldMarkForUpload ? 1 : 0,
        uploaded: 0,
        initFilename: this.currentInitFilename,
        initId: this.currentInitId,
        duration,
        discontinuity: i === 0
      };

      const dbKey = await this.storeToDb(segmentMetaPayload, segmentDataPayload);
      log.recorder("DONE saving segment ", index);
      if (shouldMarkForUpload) {
        const uploadable: UploadableSegment = { segment: segmentDataPayload, init: this.currentInit, dbKey };
        this.emitter.emit("segment-for-upload", uploadable);
      }

      this.sourceDateMs += Number(duration) * 1000;
      i++;
    }
    if (this.uploadDateRange.start && this.uploadDateRange.end && this.sourceDateMs >= this.uploadDateRange.end) {
      this.uploadDateRange.start = null;
      this.uploadDateRange.end = null;
    }

    const gapBetweenNowAndSegmentEnd = trueSegmentEndTime - this.sourceDateMs;
    // maybe cant happen (was just simulated by shifting Date, should solve device sleep etc.)
    if (gapBetweenNowAndSegmentEnd > SEGMENT_LENGTH) {
      log.recorder("Recording stalled, continuing the timeline from now", gapBetweenNowAndSegmentEnd);
      this.sourceDateMs = trueSegmentEndTime;
      this.shouldPrepareStoring = true;
      this.videoAppendInMs = 0;
    } else if (gapBetweenNowAndSegmentEnd >= APPEND_THRESHOLD_MS) {
      log.recorder(
        "Gap between now and segment end is greater then 200ms, next transcoding will append",
        gapBetweenNowAndSegmentEnd
      );
      this.videoAppendInMs = gapBetweenNowAndSegmentEnd;
    } else this.videoAppendInMs = 0;

    this.playlist.addToNextSegmentIndex(segmentsData.length);
    this.playlist.clearLastSentDeltaPlaylists();
  };

  private storeToDb = async (meta: HlsMetaItem, data: HlsDataItem) => {
    if (!this.canStore) {
      if (!this.shouldPrepareStoring) this.checkDb();
      return null;
    }
    try {
      const [, dataWrite] = await addTimeout(
        () => Promise.all([this.db.getWrite("hls-meta")(meta), this.db.getWrite("hls-data")(data)]),
        DB_TIMEOUT
      );
      return (dataWrite as { target: IDBRequest<number> }).target.result;
    } catch (err) {
      log.err(`Couldn't store ${data.filename}, not storing files until the DB answers again`, err);
      this.canStore = false;
      return null;
    }
  };

  private checkDb = async () => {
    if (this.isCheckingDb) return;
    this.isCheckingDb = true;
    try {
      await addTimeout(() => this.db.probeWrite("hls-data"), DB_TIMEOUT);
      if (!this.canStore) {
        log.recorder("DB answers again, storing files from the next chunk");
        this.shouldPrepareStoring = true;
      }
    } catch (err) {
      log.recorder("DB still doesn't answer", err);
    } finally {
      this.isCheckingDb = false;
    }
  };

  private prepareStoring = async () => {
    this.shouldPrepareStoring = false;
    try {
      await addTimeout(() => this.dbController.fillSegmentGaps(this.sourceDateIso), DB_TIMEOUT);
      await this.playlist.prepareNextUsableIndexes();
      this.hasCreatedInitFile = false;
      this.canStore = true;
    } catch (err) {
      log.err("DB doesn't answer, recording without storing files", err);
      this.canStore = false;
    }
  };
}
